/**
 * FleetManager V3 - Header Component
 * Global search, profile switcher, notifications, and user menu
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Bell, ChevronDown, CheckCircle, Building2, UserCog, BarChart3,
  LogOut, Shield, AlertTriangle, ListChecks, Sun, Moon, Monitor,
  HelpCircle
} from 'lucide-react';
import { useProfile } from '../../hooks/useProfile.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { usePipelineOptional } from '../../context/PipelineContext.jsx';
import { useThemeOptional } from '../../context/ThemeContext.jsx';
import { UnifiedSearchBar } from './UnifiedSearchBar.jsx';
import { useOrganizations } from '../../../hooks/useDataHooks.js';
import { useTour } from '../tour/index.js';
import { useVisualHelp } from '../help/VisualHelpProvider.jsx';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher.jsx';

const PROFILE_ICONS = {
  Building2, UserCog, BarChart3
};

/**
 * ProfileSwitcher - Shows current profile with optional switching
 *
 * - Superadmin/Admin: Can switch profiles (dropdown with chevron)
 * - Regular users: Static display only (no dropdown, no chevron)
 */
function ProfileSwitcher() {
  const { t } = useTranslation();
  const { profile, profiles, switchProfile } = useProfile();
  const { user, isSuperuser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  // Check if user can switch profiles (admin or superuser only)
  const canSwitchProfile = isSuperuser || user?.role === 'admin';

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    if (canSwitchProfile) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [canSwitchProfile]);

  const ProfileIcon = PROFILE_ICONS[profile.icon] || Building2;

  // Regular users: Static profile badge (no dropdown)
  if (!canSwitchProfile) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200"
        data-tour="profile-display"
        title={profile.description}
      >
        <ProfileIcon size={14} style={{ color: profile.color }} />
        <span className="text-sm text-gray-700 font-medium">{profile.name}</span>
      </div>
    );
  }

  // Admins/Superusers: Dropdown to switch profiles
  return (
    <div className="relative" ref={ref} data-tour="profile-switcher">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 border border-gray-200 hover:bg-gray-200 transition-colors"
      >
        <ProfileIcon size={14} style={{ color: profile.color }} />
        <span className="text-sm text-gray-900">{profile.name}</span>
        <ChevronDown size={12} className="text-gray-500" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="p-2">
            <div className="text-xs text-gray-500 uppercase px-2 py-1 mb-1">
              {t('header.profile.switchTitle')}
            </div>
            {Object.values(profiles).map(p => {
              const PIcon = PROFILE_ICONS[p.icon] || Building2;
              const isActive = profile.id === p.id;

              return (
                <button
                  key={p.id}
                  onClick={() => {
                    switchProfile(p.id);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-2 p-2 rounded-lg transition-colors
                    ${isActive ? 'bg-gray-100' : 'hover:bg-gray-50'}
                  `}
                >
                  <PIcon size={16} style={{ color: p.color }} />
                  <div className="flex-1 text-left">
                    <div className="text-sm text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500">{p.description}</div>
                  </div>
                  {isActive && (
                    <CheckCircle size={14} className="text-emerald-500" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function NotificationBell({ count = 0 }) {
  const { t } = useTranslation();
  return (
    <button className="relative p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors" title={t('header.notifications.title')}>
      <Bell size={18} />
      {count > 0 && (
        <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
          {count > 9 ? '9+' : count}
        </span>
      )}
    </button>
  );
}

function HelpButton() {
  const { t } = useTranslation();
  // Use VisualHelp context (always available when wrapped in provider)
  const visualHelp = useVisualHelp();

  const handleClick = () => {
    visualHelp.openResourceCenter();
  };

  const isActive = visualHelp.isResourceCenterOpen;

  return (
    <button
      onClick={handleClick}
      className={`
        p-2 rounded-lg transition-colors
        ${isActive
          ? 'text-amber-500 bg-amber-50'
          : 'text-gray-500 hover:text-amber-600 hover:bg-amber-50'
        }
      `}
      title={t('header.help.title')}
      data-tour="help-button"
    >
      <HelpCircle size={18} />
    </button>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const { user, tenant, logout, isSuperuser, isImpersonating, stopImpersonation } = useAuth();
  const pipeline = usePipelineOptional();
  const theme = useThemeOptional();
  const tour = useTour();
  const [isOpen, setIsOpen] = useState(false);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const ref = useRef(null);

  const handleReplayTour = () => {
    setIsOpen(false);
    if (tour) tour.startTour('main');
  };

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
  };

  const handleStopImpersonation = () => {
    setIsOpen(false);
    stopImpersonation();
    window.location.reload();
  };

  const handlePipelineToggle = async () => {
    if (!pipeline || pipelineLoading) return;
    setPipelineLoading(true);
    try {
      await pipeline.togglePipeline(!pipeline.pipelineEnabled);
    } catch (err) {
      console.error('Failed to toggle pipeline:', err);
    } finally {
      setPipelineLoading(false);
    }
  };

  return (
    <div className="relative" ref={ref} data-tour="user-menu">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-medium">
          {user?.first_name?.charAt(0) || user?.email?.charAt(0) || '?'}
        </div>
        <ChevronDown size={12} className="text-gray-500" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* User Info */}
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium">
                {user?.first_name?.charAt(0) || user?.email?.charAt(0) || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900 font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </div>
                <div className="text-xs text-gray-500 truncate">{user?.email}</div>
              </div>
            </div>
            {/* Role badges */}
            <div className="flex items-center gap-2 mt-2">
              <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                {user?.role}
              </span>
              {isSuperuser && (
                <span className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-600 flex items-center gap-1">
                  <Shield size={10} /> {t('header.user.superuser')}
                </span>
              )}
            </div>
          </div>

          {/* Tenant Info */}
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-500">{t('header.user.tenant')}</div>
            <div className="text-sm text-gray-900">{tenant?.name}</div>
          </div>

          {/* Impersonation Warning */}
          {isImpersonating && (
            <div className="px-3 py-2 bg-orange-50 border-b border-orange-200">
              <div className="flex items-center gap-2 text-orange-600 text-xs">
                <AlertTriangle size={12} />
                <span>{t('header.user.impersonating')}</span>
              </div>
              <button
                onClick={handleStopImpersonation}
                className="mt-1 text-xs text-orange-600 hover:text-orange-700 underline"
              >
                {t('header.user.stopImpersonation')}
              </button>
            </div>
          )}

          {/* Pipeline Toggle (admin, fleet_manager, superuser) */}
          {pipeline && (user?.role === 'admin' || user?.role === 'fleet_manager' || isSuperuser) && (
            <div className="px-3 py-2 border-b border-gray-200">
              <button
                onClick={handlePipelineToggle}
                disabled={pipelineLoading}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <ListChecks size={16} className={pipeline.pipelineEnabled ? 'text-emerald-500' : 'text-gray-400'} />
                  <span className="text-sm text-gray-900">{t('header.user.pipelineMode')}</span>
                </div>
                <div
                  className={`
                    relative w-10 h-5 rounded-full transition-colors
                    ${pipeline.pipelineEnabled ? 'bg-emerald-500' : 'bg-gray-300'}
                    ${pipelineLoading ? 'opacity-50' : ''}
                  `}
                >
                  <div
                    className={`
                      absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform
                      ${pipeline.pipelineEnabled ? 'translate-x-5' : 'translate-x-0.5'}
                    `}
                  />
                </div>
              </button>
              <div className="px-3 mt-1">
                <span className="text-xs text-gray-500">
                  {pipeline.pipelineEnabled ? t('header.user.pipelineOn') : t('header.user.pipelineOff')}
                </span>
              </div>
            </div>
          )}

          {/* Theme Toggle — hidden until all components support dark: classes (DARK_MODE_ENABLED in ThemeContext) */}
          {false && theme && (
            <div className="px-3 py-2 border-b border-gray-200">
              <div className="flex items-center justify-between gap-2 px-3 py-2">
                <div className="flex items-center gap-2">
                  {theme.effectiveTheme === 'dark' ? (
                    <Moon size={16} className="text-blue-500" />
                  ) : (
                    <Sun size={16} className="text-amber-500" />
                  )}
                  <span className="text-sm text-gray-900">{t('header.user.theme')}</span>
                </div>
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => theme.setTheme('light')}
                    className={`p-1.5 rounded-md transition-colors ${
                      theme.themeMode === 'light' ? 'bg-white shadow text-amber-500' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title={t('header.user.themeLight')}
                  >
                    <Sun size={14} />
                  </button>
                  <button
                    onClick={() => theme.setTheme('dark')}
                    className={`p-1.5 rounded-md transition-colors ${
                      theme.themeMode === 'dark' ? 'bg-white shadow text-blue-500' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title={t('header.user.themeDark')}
                  >
                    <Moon size={14} />
                  </button>
                  <button
                    onClick={() => theme.setTheme('system')}
                    className={`p-1.5 rounded-md transition-colors ${
                      theme.themeMode === 'system' ? 'bg-white shadow text-gray-700' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title={t('header.user.themeSystem')}
                  >
                    <Monitor size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Menu Items */}
          <div className="p-2">
            {tour && (
              <button
                onClick={handleReplayTour}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <HelpCircle size={16} className="text-amber-500" />
                <span className="text-sm">{t('header.user.replayTour')}</span>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={16} />
              <span className="text-sm">{t('header.user.logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// =============================================================================
// ORG SWITCHER — Groupe / multi-org : « Toutes les organisations » ou une filiale.
// Visible seulement si l'utilisateur a accès à plus d'une organisation.
// =============================================================================

function OrgSwitcher() {
  const { t } = useTranslation();
  const { accessibleOrgs, activeOrg, setActiveOrg } = useAuth();
  const { data: organizations = [] } = useOrganizations();

  if (!accessibleOrgs || accessibleOrgs.length < 2) return null;

  const byCode = new Map(organizations.map(o => [o.code, o]));
  const options = accessibleOrgs.map(code => ({
    code,
    label: byCode.get(code)?.name || code,
    country: byCode.get(code)?.country || null,
  }));

  return (
    <div className="flex items-center gap-2 px-2 py-1 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800"
         title={t('header.org.title')}>
      <Building2 className="w-4 h-4 text-gray-500" />
      <select
        value={activeOrg || 'all'}
        onChange={(e) => setActiveOrg(e.target.value)}
        className="bg-transparent text-sm text-gray-800 dark:text-gray-100 outline-none cursor-pointer max-w-[220px]"
        aria-label={t('header.org.ariaLabel')}
      >
        <option value="all">{t('header.org.all', { count: accessibleOrgs.length })}</option>
        {options.map(o => (
          <option key={o.code} value={o.code}>
            {o.label}{o.country ? ` · ${o.country}` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Header({ criticalAlerts = 0, onNavigate }) {
  return (
    <header className="h-12 bg-white dark:bg-slate-900/80 dark:backdrop-blur-sm border-b border-gray-200 dark:border-slate-800 flex items-center justify-between px-4">
      <UnifiedSearchBar onNavigate={onNavigate} />

      <div className="flex items-center gap-3">
        <OrgSwitcher />
        <LanguageSwitcher />
        <ProfileSwitcher />
        <HelpButton />
        <NotificationBell count={criticalAlerts} />
        <UserMenu />
      </div>
    </header>
  );
}

export default Header;
